import { permissionNames as P } from "../auth/permissions.generated";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { UserProfile } from "./UserProfile";
import { useAuth0 } from "@auth0/auth0-react";
import { useSharedPermissions } from "../contexts/PermissionsContext";
import { type UserPermissions } from "../hooks/useAuth";
import {
  BarChart3,
  Boxes,
  BrainCircuit,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Database,
  DatabaseZap,
  FilePlus2,
  FileSpreadsheet,
  FileStack,
  FlaskConical,
  Gavel,
  Home,
  KanbanSquare,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  Mail,
  Megaphone,
  MessagesSquare,
  Orbit,
  Palette,
  PackageSearch,
  SearchCheck,
  Presentation,
  Settings,
  Sparkles,
  Target,
  UserSearch,
  Wand2,
  X,
} from "lucide-react";
import { cn } from "./ui/utils";
import "./Navigation.css";
import { useFeedback } from './feedback/FeedbackContext';

export type NavigationItem =
  | "notifications"
  | "feedback"
  | "dashboard"
  | "demand-dashboard"
  | "create-demand"
  | "demand-backlog"
  | "board-governance"
  | "project-portfolio"
  | "reports"
  | "campaign-dashboard"
  | "create-campaign"
  | "edit-campaign"
  | "campaign-showcase"
  | "article-assistant-chat"
  | "brand-management"
  | "campaign-ad-variations"
  | "styled-images-studio"
  | "target-profile-management"
  | "product-dashboard"
  | "business-rule-management"
  | "bulk-pdf-generation"
  | "catalog-management"
  | "product-comparer"
  | "product-collections"
  | "product-search"
  | "product-detail"
  | "product-sync"
  | "concept-dashboard"
  | "create-concept"
  | "concept-detail"
  | "edit-concept"
  | "hubspot-dashboard"
  | "hubspot-backfilling"
  | "hubspot-backfill-forms"
  | "hubspot-email-metrics"
  | "settings"
  | "profile";

interface NavigationProps {
  currentView: NavigationItem;
  onNavigate: (view: NavigationItem, campaignId?: string, conceptId?: number) => void;
  pendingChanges?: boolean;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
}

interface NavigationSubItem {
  id: NavigationItem;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  permission?: keyof UserPermissions;
}

interface NavigationGroup {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  isCollapsible: boolean;
  items: NavigationSubItem[];
}

const navigationStructure: NavigationGroup[] = [
  {
    id: "main",
    label: "Main",
    icon: LayoutDashboard,
    isCollapsible: false,
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: Home,
        description: "Overview and key metrics",
        permission: undefined,
      },
    ],
  },
  {
    id: "demand-management",
    label: "Demand Management",
    icon: Target,
    isCollapsible: true,
    items: [
      {
        id: "demand-dashboard",
        label: "Dashboard",
        icon: LayoutGrid,
        description: "Demand management overview",
        permission: "canAccessDemandDashboard",
      },
      {
        id: "create-demand",
        label: "Create Demand",
        icon: FilePlus2,
        description: "Submit new demand request",
        permission: "canCreateDemands",
      },
      {
        id: "demand-backlog",
        label: "Demand Backlog",
        icon: ClipboardList,
        description: "Manage submitted demands",
        permission: "canViewDemandBacklog",
      },
      {
        id: "board-governance",
        label: "Board Governance",
        icon: Gavel,
        description: "Governance and oversight",
        permission: "canViewBoardGovernance",
      },
      {
        id: "project-portfolio",
        label: "Project Portfolio",
        icon: KanbanSquare,
        description: "Active projects and pipeline",
        permission: "canViewProjectPortfolio",
      },
      {
        id: "reports",
        label: "Reports",
        icon: BarChart3,
        description: "Analytics and reporting",
        permission: "canViewReports",
      },
    ],
  },
  {
    id: "campaign-management",
    label: "Campaign Management",
    icon: Megaphone,
    isCollapsible: true,
    items: [
      {
        id: "campaign-dashboard",
        label: "Dashboard",
        icon: Presentation,
        description: "Campaign management overview",
        permission: "canAccessCampaignManagement",
      },
      {
        id: "campaign-showcase",
        label: "Campaign Showcase",
        icon: LayoutDashboard,
        description: "Share campaign story pages",
        permission: "canAccessCampaignManagement",
      },
      {
        id: "create-campaign",
        label: "Create Campaign",
        icon: Sparkles,
        description: "Create new marketing campaign",
        permission: "canAddCampaigns",
      },
      {
        id: "target-profile-management",
        label: "Target Profiles",
        icon: UserSearch,
        description: "Manage B2B and B2C audiences",
        permission: "canAccessCampaignManagement",
      },
      {
        id: "brand-management",
        label: "Brands",
        icon: Palette,
        description: "Manage brand guidelines and tone",
        permission: "canAccessCampaignManagement",
      },
      {
        id: "campaign-ad-variations",
        label: "Ad Variations",
        icon: Wand2,
        description: "Generate AI-assisted ad creatives",
        permission: "canAccessCampaignManagement",
      },
      {
        id: "styled-images-studio",
        label: "Styled Images",
        icon: Sparkles,
        description: "Extract style references and generate on-brand visuals",
        permission: "canAccessCampaignManagement",
      },
    ],
  },
  {
    id: "product-management",
    label: "Product Management",
    icon: Boxes,
    isCollapsible: true,
    items: [
      {
        id: "product-dashboard",
        label: "Dashboard",
        icon: PackageSearch,
        description: "Product management overview",
        permission: undefined,
      },
      {
        id: "product-search",
        label: "Search",
        icon: SearchCheck,
        description: "Search and filter the product catalogue",
        permission: "canReadProductSearch",
      },
      {
        id: "article-assistant-chat",
        label: "Article Assistant",
        icon: MessagesSquare,
        description: "Chat with AI about products and articles",
        permission: "canReadProductSearch",
      },
      {
        id: "product-collections",
        label: "Collections",
        icon: Boxes,
        description: "Manage your shared product collections",
        permission: "canReadCollections",
      },
      {
        id: "product-sync",
        label: "Search Synchronization",
        icon: DatabaseZap,
        description: "Run and monitor the InRiver search synchronization",
        permission: "canManageProductSearchSync",
      },
      {
        id: "business-rule-management",
        label: "Business Rules",
        icon: ClipboardList,
        description: "Manage product quality validation rules",
        permission: "canGeneratePDFs",
      },
      {
        id: "bulk-pdf-generation",
        label: "Bulk PDF Generation",
        icon: FileStack,
        description: "Generate PDFs in bulk",
        permission: "canGeneratePDFs",
      },
      {
        id: "catalog-management",
        label: "Catalog Management",
        icon: Presentation,
        description: "Browse queries and generate PowerPoint catalogs",
        permission: "canGeneratePDFs",
      },
    ],
  },
  {
    id: "concept-management",
    label: "Concept Management",
    icon: BrainCircuit,
    isCollapsible: true,
    items: [
      {
        id: "concept-dashboard",
        label: "Concepts Overview",
        icon: Lightbulb,
        description: "Review concept pipeline and status",
        permission: "canAccessConceptManagement",
      },
      {
        id: "create-concept",
        label: "Create Concept",
        icon: FlaskConical,
        description: "Capture a new product concept",
        permission: "canManageConcepts",
      },
    ],
  },
  {
    id: "hubspot-management",
    label: "HubSpot Management",
    icon: Database,
    isCollapsible: true,
    items: [
      {
        id: "hubspot-dashboard",
        label: "Dashboard",
        icon: Orbit,
        description: "HubSpot management overview",
        permission: "canManageHubSpot",
      },
      {
        id: "hubspot-backfilling",
        label: "Backfill Contacts",
        icon: DatabaseZap,
        description: "Manage HubSpot contact backfilling",
        permission: "canManageHubSpot",
      },
      {
        id: "hubspot-backfill-forms",
        label: "Backfill Forms",
        icon: FileSpreadsheet,
        description: "Manage HubSpot form backfilling",
        permission: "canManageHubSpot",
      },
      {
        id: "hubspot-email-metrics",
        label: "Email Metrics",
        icon: Mail,
        description: "Backfill and retrieve HubSpot email metrics",
        permission: "canManageHubSpot",
      },
    ],
  },
  {
    id: "system",
    label: "Settings",
    icon: Settings,
    isCollapsible: false,
    items: [
      {
        id: "settings",
        label: "Settings",
        icon: Settings,
        description: "Manage Auth0 users, roles, and permissions",
        permission: "isAdmin",
      },
    ],
  },
];

const findGroupForView = (view: NavigationItem) =>
  navigationStructure.find((group) => group.items.some((item) => item.id === view));

const navigationCapabilities: Record<string, string> = {"brand-management": P.CampaignBrandRead, "target-profile-management": P.CampaignTargetProfileRead, "styled-images-studio": P.CampaignStyleRead, "campaign-ad-variations": P.CampaignCreativeGenerate, "business-rule-management": P.ProductBusinessRuleRead, "catalog-management": P.ProductCatalogTemplateRead, "product-comparer": P.ProductComparisonRead};

interface NavigationTreeItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  view?: NavigationItem;
  children?: NavigationTreeItem[];
}

interface NavigationColumn {
  heading: string;
  items: NavigationTreeItem[];
}

export function Navigation({
  currentView,
  onNavigate,
  pendingChanges = false,
  isMobileOpen,
  onCloseMobile,
}: NavigationProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<NavigationItem | null>(null);
  const [activePath, setActivePath] = useState<string[]>([]);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { user } = useAuth0();
  const permissions = useSharedPermissions();
  const feedback = useFeedback();
  const effectiveCurrentView =
    currentView === "concept-detail" || currentView === "edit-concept" ? "concept-dashboard" : currentView === "product-detail" ? "product-search" : currentView;
  const activeGroupId = findGroupForView(effectiveCurrentView)?.id;

  const navigationTree = useMemo<NavigationTreeItem[]>(() => {
    const canShowGroup = (groupId: string) => {
      if (groupId === "demand-management") return permissions.canAccessDemandManagement;
      if (groupId === "campaign-management") return permissions.canAccessCampaignManagement;
      if (groupId === "hubspot-management") return permissions.canManageHubSpot;
      if (groupId === "concept-management") return permissions.canAccessConceptManagement;
      return true;
    };

    const groups: NavigationGroup[] = [...navigationStructure.filter(group => group.id !== "system"),
      ...([P.NotificationCampaignRead, P.NotificationTargetProfileRead].some(p => permissions.allows?.(p)) ? [{ id: 'notifications', label: 'Push notifications', icon: Megaphone, isCollapsible: false, items: [{ id: 'notifications' as NavigationItem, label: 'Push notifications', icon: Megaphone, description: 'Compose notifications and manage audiences' }] }] : []),
      ...(feedback.permissions.some(p => ['feedback:record:read:any', 'feedback:record:read:assigned', 'feedback:record:read:team', 'feedback:settings:manage', 'feedback:report:read:any', 'feedback:report:read:team'].includes(p))
        ? [{ id: 'feedback', label: 'Feedback', icon: MessagesSquare, isCollapsible: false, items: [{ id: 'feedback' as NavigationItem, label: 'Feedback', icon: MessagesSquare, description: 'Review and manage application feedback' }] }] : []),
      ...navigationStructure.filter(group => group.id === "system")];
    return groups.flatMap((group) => {
      if (!canShowGroup(group.id)) return [];

      const children: NavigationTreeItem[] = group.items
        .filter((item) => item.id === 'business-rule-management' && [P.ProductFieldRestrictionRead, P.ProductInriverRoleAssignmentRead].some(capability => permissions.allows?.(capability) === true) ? true : navigationCapabilities[item.id]
          ? permissions.allows?.(navigationCapabilities[item.id]) === true
          : !item.permission || Boolean(permissions[item.permission]))
        .map((item) => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
          description: item.description,
          view: item.id,
        }));

      if (children.length === 0) return [];

      if (!group.isCollapsible && children.length === 1) {
        return children;
      }

      return [{
        id: group.id,
        label: group.label,
        icon: group.icon,
        children,
      }];
    });
  }, [permissions, feedback.permissions]);

  const columns = useMemo<NavigationColumn[]>(() => {
    const result: NavigationColumn[] = [{ heading: "Applications", items: navigationTree }];
    let items = navigationTree;

    for (const selectedId of activePath) {
      const selected = items.find((item) => item.id === selectedId);
      if (!selected?.children?.length) break;
      result.push({ heading: selected.label, items: selected.children });
      items = selected.children;
    }

    return result;
  }, [activePath, navigationTree]);

  useEffect(() => {
    if (!isMobileOpen) {
      setActivePath([]);
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseMobile();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileOpen, onCloseMobile]);

  const handleNavigationClick = (targetView: NavigationItem) => {
    if (pendingChanges && currentView !== targetView) {
      setPendingNavigation(targetView);
      setShowConfirmDialog(true);
    } else {
      onNavigate(targetView);
      if (isMobileOpen) {
        onCloseMobile();
      }
    }
  };

  const handleConfirmNavigation = () => {
    if (pendingNavigation) {
      onNavigate(pendingNavigation);
      if (isMobileOpen) {
        onCloseMobile();
      }
      setPendingNavigation(null);
    }
    setShowConfirmDialog(false);
  };

  const handleCancelNavigation = () => {
    setPendingNavigation(null);
    setShowConfirmDialog(false);
  };

  const selectItem = (item: NavigationTreeItem, level: number) => {
    if (item.children?.length) {
      setActivePath((path) => [...path.slice(0, level), item.id]);
      return;
    }

    if (item.view) handleNavigationClick(item.view);
  };

  const goBack = (level: number) => {
    setActivePath((path) => path.slice(0, Math.max(0, level - 1)));
  };

  return (
    <>
      {isMobileOpen && (
        <div className="nav-cascade-shell">
          <div
            className="nav-cascade-backdrop"
            onClick={onCloseMobile}
            aria-hidden="true"
          />
          <nav id="main-navigation" aria-label="Main navigation" className="nav-cascade-menu">
            {columns.map((column, level) => {
              const isCurrent = level === columns.length - 1;

              return (
                <section
                  key={level === 0 ? "root" : activePath[level - 1]}
                  aria-labelledby={`navigation-level-${level}`}
                  className={cn(
                    "nav-cascade-panel",
                    isCurrent ? "nav-cascade-panel--current" : "nav-cascade-panel--previous",
                  )}
                >
                  <header className="nav-cascade-header">
                    {level === 0 ? (
                      <img
                        src="/lantmannen-unibake.svg"
                        alt="Lantmännen Unibake"
                        className="h-10 w-auto max-w-[210px]"
                      />
                    ) : (
                      <div className="flex min-w-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() => goBack(level)}
                          data-track-id="navigation_back"
                          className="nav-cascade-back"
                          aria-label={`Back to ${columns[level - 1].heading}`}
                        >
                          <ChevronLeft className="h-5 w-5" />
                          <span>Back</span>
                        </button>
                        <h2 id={`navigation-level-${level}`} className="nav-cascade-title">
                          {column.heading}
                        </h2>
                      </div>
                    )}
                    <button
                      ref={level === 0 ? closeButtonRef : undefined}
                      type="button"
                      onClick={onCloseMobile}
                      data-track-id="navigation_close"
                      className="nav-cascade-close"
                    >
                      <X className="h-5 w-5" />
                      <span className="sr-only">Close navigation</span>
                    </button>
                  </header>

                  <div className="nav-cascade-scroll">
                    <p id={level === 0 ? "navigation-level-0" : undefined} className="nav-cascade-eyebrow">
                      {column.heading}
                    </p>
                    <ul className="space-y-1">
                      {column.items.map((item) => {
                        const Icon = item.icon;
                        const hasChildren = Boolean(item.children?.length);
                        const isSelected = activePath[level] === item.id;
                        const isActive = item.view === effectiveCurrentView || item.id === activeGroupId;

                        return (
                          <li key={item.id}>
                            <button
                              type="button"
                              onClick={() => selectItem(item, level)}
                              data-track-id={`navigation_${item.id.replace(/-/g, '_')}`}
                              aria-expanded={hasChildren ? isSelected : undefined}
                              className={cn(
                                "nav-cascade-item",
                                (isSelected || isActive) && "nav-cascade-item--active",
                              )}
                            >
                              <Icon className="h-5 w-5 shrink-0" />
                              <span className="min-w-0 flex-1 text-left">
                                <span className="block truncate font-medium">{item.label}</span>
                                {item.description && (
                                  <span className="nav-cascade-description">{item.description}</span>
                                )}
                              </span>
                              {hasChildren && <ChevronRight className="h-5 w-5 shrink-0" />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {level === 0 && (
                    <footer className="nav-cascade-footer">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-sidebar-foreground">
                          {user?.name || user?.email || "User"}
                        </p>
                        <p className="truncate text-xs text-sidebar-foreground/60">
                          {permissions.isAdmin ? "Administrator" : "User"}
                        </p>
                      </div>
                      <UserProfile onNavigate={(view) => handleNavigationClick(view)} />
                    </footer>
                  )}
                </section>
              );
            })}
          </nav>
        </div>
      )}

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes that will be lost if you continue. Are you sure you want to navigate away from this page?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelNavigation}>Stay on Page</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmNavigation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
